import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import colors from '../../styles/colors';
import { Z_INDEX } from '../../styles/variables';
import { focusState } from '../../styles/mixins';
import { vars } from '../../theme';
import { applyOpacity } from '../../utils/color.utils';
import { TTextAlignment } from '../HBText/HBText.types';
import { getAlignment } from '../../utils/alignment.utils';

export interface IPopoverProps {
    $isOpen: boolean;
    $offsetX: number;
    $offsetY: number;
    $keepOpenOnHover: boolean;
    $maxWidth?: string;
}

const ARROW_SIZE = 12;

const cssForLeftArrow = css`
    top: calc(50% - ${ARROW_SIZE / 2}px);
    box-shadow: -4px 4px 6px ${applyOpacity('#17172C', 8)};
`;

const cssForRightArrow = css`
    top: calc(50% - ${ARROW_SIZE / 2}px);
    box-shadow: 4px -4px 6px ${applyOpacity('#17172C', 8)};
`;

const cssForTopArrow = css`
    left: calc(50% - ${ARROW_SIZE / 2}px);
    box-shadow: -4px -4px 6px ${applyOpacity('#17172C', 8)};
`;

const cssForBottomArrow = css`
    left: calc(50% - ${ARROW_SIZE / 2}px);
    box-shadow: 4px 4px 6px ${applyOpacity('#17172C', 8)};
`;

const keepOpenOnHoverBlockStyle = (props: IPopoverProps) => css`
    width: 100%;
    height: ${ARROW_SIZE + props.$offsetY}px;
    left: 0;
`;

const keepOpenOnHoverInlineStyle = (props: IPopoverProps) => css`
    height: 100%;
    width: ${ARROW_SIZE + props.$offsetX}px;
    top: 0;
`;

const keepOpenOnHoverBlockPosition = (props: IPopoverProps) =>
    `${-ARROW_SIZE - props.$offsetY}px`;

const keepOpenOnHoverInlinePosition = (props: IPopoverProps) =>
    `${-ARROW_SIZE - props.$offsetX}px`;

export const popoverThemes = {
    popover: {
        background: vars.colors.surface.container,
        boxShadow: vars.shadow['40'],
        minWidth: '50px',
        minHeight: '50px',
        padding: '20px'
    },

    darkTooltip: {
        color: vars.colors.text.invertedPrimary,
        background: vars.colors.surface.itemInverted,
        boxShadow: vars.shadow['40'],
        padding: '12px',
        cursor: 'auto'
    },

    lightTooltip: {
        color: vars.colors.text.primary,
        background: vars.colors.surface.container,
        boxShadow: vars.shadow['40'],
        padding: '12px',
        cursor: 'auto'
    },

    menu: {
        background: vars.colors.surface.container,
        boxShadow: vars.shadow['40'],
        minWidth: '50px',
        minHeight: '50px',
        padding: '8px 0'
    },

    adviceTooltip: {
        // Unsupported: colors.blue50 — no semantic equivalent (see .claude/rules/styling.md)
        background: colors.blue50,
        boxShadow: vars.shadow['40'],
        color: vars.colors.text.invertedPrimary,
        padding: '12px 12px 12px 44px',
        cursor: 'auto'
    },

    upgradeTooltip: {
        background: vars.colors.surface.upgrade,
        boxShadow: vars.shadow['40'],
        color: vars.colors.text.primary,
        padding: '12px 12px 12px 34px',
        cursor: 'auto'
    },

    calloutNeutral: {
        boxShadow: vars.shadow['40'],
        minWidth: '50px',
        minHeight: '50px',
        padding: '20px',
        background: vars.colors.surface.container
    },

    calloutAccent: {
        boxShadow: vars.shadow['40'],
        minWidth: '50px',
        minHeight: '50px',
        padding: '20px',
        // Unsupported: colors.purple800 — no semantic equivalent (see .claude/rules/styling.md)
        background: colors.purple800
    }
};

function getPopoverTheme(theme: string) {
    if (!popoverThemes[theme]) {
        return null;
    }

    const {
        background,
        boxShadow,
        color,
        minHeight,
        minWidth,
        padding,
        cursor
    } = popoverThemes[theme];

    return css`
        background: ${background};
        box-shadow: ${boxShadow};
        color: ${color};
        min-height: ${minHeight};
        min-width: ${minWidth};
        padding: ${padding};
        cursor: ${cursor};
    `;
}

function getArrowTheme(theme: string) {
    const { background } = popoverThemes[theme];

    return css`
        background: ${background};
    `;
}

export const Popover = styled(motion.div)<IPopoverProps>`
    position: absolute;
    border-radius: ${vars.radius.md};
    font-size: 14px;
    max-width: ${({ $maxWidth }) => $maxWidth || '500px'};
    font-family: ${vars.fonts.body};
    z-index: ${Z_INDEX.POPOVER};
    ${({ theme }) => getPopoverTheme(theme)}

    pointer-events: none;

    ${({ $isOpen, $keepOpenOnHover }) => css`
        ${$isOpen &&
        css`
            pointer-events: auto;
        `}
        ${$keepOpenOnHover &&
        css`
            &:before {
                position: absolute;
                content: '';
            }
        `}
    `}
    &[data-popper-placement^='top'] {
        &:before {
            ${keepOpenOnHoverBlockStyle};
            bottom: ${keepOpenOnHoverBlockPosition};
        }
        > [data-popper-arrow] {
            bottom: ${ARROW_SIZE / 2}px;
        }
    }
    &[data-popper-placement^='top'] > [data-popper-arrow]:after {
        ${cssForBottomArrow}
    }
    &[data-popper-placement^='right'] {
        &:before {
            ${keepOpenOnHoverInlineStyle};
            left: ${keepOpenOnHoverInlinePosition};
        }
        > [data-popper-arrow] {
            left: ${-ARROW_SIZE / 2}px;
        }
    }
    &[data-popper-placement^='right'] > [data-popper-arrow]:after {
        ${cssForLeftArrow}
    }
    &[data-popper-placement^='bottom'] {
        &:before {
            ${keepOpenOnHoverBlockStyle};
            top: ${keepOpenOnHoverBlockPosition};
        }
        > [data-popper-arrow] {
            top: ${-ARROW_SIZE / 2}px;
        }
    }
    &[data-popper-placement^='bottom'] > [data-popper-arrow]:after {
        ${cssForTopArrow}
    }
    &[data-popper-placement^='left'] {
        &:before {
            ${keepOpenOnHoverInlineStyle};
            right: ${keepOpenOnHoverInlinePosition};
        }
        > [data-popper-arrow] {
            right: ${ARROW_SIZE / 2}px;
        }
    }
    &[data-popper-placement^='left'] > [data-popper-arrow]:after {
        ${cssForRightArrow}
    }

    // Clicking plain content focuses the panel (tabindex -1); that focus needs no ring.
    &:focus:not(:focus-visible) {
        outline: none;
    }

    // Never hide a popover that holds focus: an on-screen keyboard can cover the target, and
    // hiding the popover would drop focus from its input and close that keyboard again.
    &[data-popper-reference-hidden]:not(:focus-within) {
        visibility: hidden;
        pointer-events: none;
    }
`;

export const Arrow = styled.div`
    &:after {
        content: ' ';
        position: absolute;
        z-index: 1;
        transform: rotate(45deg);
        width: ${ARROW_SIZE}px;
        height: ${ARROW_SIZE}px;
        transform-origin: center center;
        ${({ theme }) => getArrowTheme(theme)}
    }
`;

export const TargetContainer = styled.div`
    display: inline;
    ${focusState}
`;

export const ActionsContainer = styled.div<{
    $actionsAlignment: TTextAlignment;
    $marginTop: number;
    $isVertical: boolean;
}>`
    display: flex;
    gap: 8px;

    ${({ $isVertical }) =>
        $isVertical &&
        css`
            flex-direction: column-reverse;
            gap: 12px;
        `}

    ${({ $actionsAlignment }) => getAlignment($actionsAlignment)}
    margin-top: ${({ $marginTop }) => `${$marginTop}px`}
`;

export const ActionSpacer = styled.div<{
    $isVertical: boolean;
}>`
    ${({ $isVertical }) =>
        $isVertical &&
        css`
            width: 100%;
        `}
`;
